import styled from '@emotion/styled';
import { Trans, useTranslation } from 'react-i18next';
import { Button, Card, FormGroup } from 'react-bootstrap';
import {
  Alert,
  BodyLarge,
  BodySmall,
  CardBody,
  HeadingMedium,
  Severity,
  theme,
  usePath,
  ChevronRightIcon,
} from '@arnold/common';
import { CREDITS_USAGE_THRESHOLD, ProductCode, getLocalizedDateWithoutLeadingZeroes } from '@arnold/core';
import { FormGroupLabel } from '../../components/Common';
import { EDITION } from '../../lib/common';
import { OrganizationQuery, OrganizationSettingQuery } from '../../generated/hooks';
import { AppLink } from '../../components/StyledComponents';

interface IProps {
  productHistory: NonNullable<NonNullable<OrganizationSettingQuery['organization']>['productVersionHistory']>;
  productVersion: NonNullable<OrganizationQuery['organization']>['productVersion'];
  activeRespondentsInCurrentPeriodCount?: number | null;
  isFromHistoryPage?: boolean;
}

interface IPropsEdition {
  edition?: string;
  duration?: string;
  licenceCount?: number;
  usedCount?: number | null;
  activePeriodEnd?: string | null;
  hasPreviousCreditEdition?: boolean;
  isFromHistoryPage?: boolean;
}

const StyledBodySmall = styled(BodySmall)`
  display: inline-block;
`;

const FormColumns = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: ${theme.spacing.f};

  @media (min-width: ${theme.breakpoint.small}) {
    width: auto;
    flex-direction: row;
    gap: ${theme.spacing.f};
  }
`;

const TextValue = styled.div`
  margin-left: ${theme.spacing.f};
  margin-top: ${theme.spacing.d};
`;

const Heading = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
`;

const EditionContainer = styled.div`
  display: flex;
`;

const EditionList = styled.div`
  flex: 1;
`;

const StyledLink = styled(AppLink)`
  margin-left: ${theme.spacing.f};
  display: inline-block;
  align-items: center;
  &:hover,
  &:focus,
  &:active {
    text-decoration: none;
  }

  svg {
    > path {
      fill: ${theme.colors.text.secondary};
    }
  }
`;

const Edition = ({ edition, duration, licenceCount, usedCount, activePeriodEnd, isFromHistoryPage }: IPropsEdition) => {
  const { t, i18n } = useTranslation('editionSettings');
  const { transactionHistoryPath } = usePath();
  return (
    <FormColumns>
      <FormGroup className="col p-0">
        <FormGroupLabel style={{ marginLeft: 0 }}>{t('edition')}</FormGroupLabel>
        <TextValue style={{ marginLeft: 0 }} data-cy="admin-edition">
          <BodyLarge bold={true}>
            {EDITION[edition as keyof typeof EDITION]?.name ? t(`label-${edition}`) : edition}
          </BodyLarge>
        </TextValue>
      </FormGroup>
      <FormGroup className="col p-0">
        <FormGroupLabel>{t('duration')}</FormGroupLabel>
        <TextValue>
          <BodyLarge bold={true}> {duration}</BodyLarge>
        </TextValue>
      </FormGroup>
      <FormGroup className="col p-0" style={{ flexBasis: 'fit-content' }}>
        <FormGroupLabel>{t(edition !== ProductCode.Credits ? 'licenceCount' : 'credits')}</FormGroupLabel>
        <TextValue data-cy="admin-licence-count">
          <BodyLarge bold={true} style={{ display: 'inline-block', marginRight: theme.spacing.d }}>
            {isFromHistoryPage ? licenceCount || 0 : (licenceCount || 0) - (usedCount || 0)}
          </BodyLarge>
          {!isFromHistoryPage && (
            <BodySmall style={{ display: 'inline-block', color: theme.colors.text.secondary }}>
              {' '}
              / {licenceCount}{' '}
              {edition === ProductCode.Business
                ? t('renewInfo', {
                    to: getLocalizedDateWithoutLeadingZeroes(new Date(activePeriodEnd || ''), i18n.language),
                  })
                : ''}
            </BodySmall>
          )}
          {!isFromHistoryPage && (
            <StyledLink to={transactionHistoryPath.createPath()}>
              <StyledBodySmall color={theme.colors.text.secondary}>{t('showHistory')}</StyledBodySmall>{' '}
              <ChevronRightIcon width={20} height={20} fill={theme.colors.text.secondary} />
            </StyledLink>
          )}
        </TextValue>
      </FormGroup>
    </FormColumns>
  );
};

const EditionOverviewCard = ({
  productHistory,
  productVersion,
  activeRespondentsInCurrentPeriodCount,
  isFromHistoryPage = false,
}: IProps) => {
  const { t, i18n } = useTranslation('editionSettings');

  const activeEdition = productVersion;

  if (!activeEdition) {
    return null;
  }

  const nextEditions = productHistory
    .filter((product) => product.validFrom >= activeEdition?.validTo)
    .sort((a, b) => new Date(a.validFrom).getTime() - new Date(b.validFrom).getTime());

  const hasPreviousCreditEdition = !!productHistory.find(
    (pv) => pv.product === ProductCode.Credits && pv.validTo <= activeEdition?.validFrom,
  );

  const messages = nextEditions.map((pv) => {
    if (pv.product === ProductCode.Free) {
      return t('downgradeInfo', {
        from: getLocalizedDateWithoutLeadingZeroes(new Date(pv.validFrom), i18n.language),
      });
    }
    if (pv.product === ProductCode.Business) {
      return t('businessInfo', {
        licenceCount: pv.licenceCount,
        from: getLocalizedDateWithoutLeadingZeroes(new Date(pv.validFrom), i18n.language),
      });
    }
    if (pv.product === ProductCode.Credits) {
      return (
        <Trans
          i18nKey={
            activeEdition.product === pv.product ? `editionSettings:moreCreditsInfo` : `editionSettings:creditsInfo`
          }
          values={{
            licenceCount: pv.boughtCredits,
            from: getLocalizedDateWithoutLeadingZeroes(new Date(pv.validFrom), i18n.language),
          }}
          components={[<strong />]}
        />
      );
    }
    return '';
  });
  if (messages.length === 0 && activeEdition.product !== ProductCode.Free) {
    messages.push(
      t('downgradeInfo', {
        from: getLocalizedDateWithoutLeadingZeroes(new Date(activeEdition.validTo), i18n.language),
      }),
    );
  }

  if (activeEdition.product === ProductCode.Credits && activeEdition.transferredCredits) {
    messages.unshift(
      <Trans
        i18nKey={`editionSettings:transferredCreditsInfo`}
        values={{
          licenceCount: activeEdition.transferredCredits,
        }}
        components={[<strong />]}
      />,
    );
  }

  const usedCredits = activeEdition.usedCredits || 0;
  const boughtCredits = activeEdition.boughtCredits || 0;
  const transferredCredits = activeEdition.transferredCredits || 0;
  const totalCredits = boughtCredits + transferredCredits;

  const isLimitExceeded = usedCredits > totalCredits;
  const isNearLimit = usedCredits > totalCredits * CREDITS_USAGE_THRESHOLD;

  return (
    <Card data-cy="admin-order-arnold-card">
      <CardBody>
        {!isFromHistoryPage && (
          <Heading>
            <HeadingMedium margin={`0 0 ${theme.spacing.h} 0`}>{t('activeEdition')}</HeadingMedium>
            <div>
              <Button
                variant="primary"
                type="submit"
                onClick={() => window.open(t('showPricingUrl'), '_blank')}
                data-cy="admin-order-button"
              >
                {t('showPricing')}
              </Button>
            </div>
          </Heading>
        )}
        <EditionContainer>
          <EditionList>
            {!isFromHistoryPage && (
              <>
                {activeEdition.product === ProductCode.Credits && isLimitExceeded && (
                  <Alert dataCy="edition-alert-overLimit" severity={Severity.DANGER} className="mb-4">
                    {t('overLimit', {
                      credits: usedCredits - totalCredits,
                    })}
                  </Alert>
                )}
                {activeEdition.product === ProductCode.Credits &&
                  !!activeEdition.usedCredits &&
                  new Date(activeEdition.validTo) > new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) &&
                  isNearLimit &&
                  !isLimitExceeded && (
                    <Alert dataCy="edition-alert-lastCredits" severity={Severity.WARNING} className="mb-4">
                      {t('lastCredits', {
                        credits: totalCredits - usedCredits,
                      })}
                    </Alert>
                  )}
                {activeEdition.validTo &&
                  activeEdition.product !== ProductCode.Free &&
                  new Date(activeEdition.validTo) <= new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) && (
                    <Alert dataCy="edition-alert-subscriptionEnds" severity={Severity.WARNING} className="mb-4">
                      {t('subscriptionEnds')}
                    </Alert>
                  )}
                {activeEdition.product === ProductCode.Free && (
                  <Alert dataCy="edition-alert-upgradeInfo" severity={Severity.WARNING} className="mb-4">
                    {t('upgradeInfo')}
                  </Alert>
                )}
              </>
            )}
            <Edition
              usedCount={
                activeEdition.product === ProductCode.Credits
                  ? activeEdition.usedCredits || 0
                  : activeRespondentsInCurrentPeriodCount
              }
              edition={activeEdition.product}
              licenceCount={
                activeEdition.product === ProductCode.Credits
                  ? (activeEdition.boughtCredits || 0) + (activeEdition.transferredCredits || 0)
                  : activeEdition.licenceCount || EDITION.FREE.licenceCount
              }
              activePeriodEnd={activeEdition.activePeriodEnd || activeEdition.validTo}
              duration={
                isFromHistoryPage
                  ? `${getLocalizedDateWithoutLeadingZeroes(
                      new Date(activeEdition.validFrom),
                      i18n.language,
                    )} - ${getLocalizedDateWithoutLeadingZeroes(new Date(activeEdition.validTo), i18n.language)}`
                  : activeEdition.activePeriodEnd
                    ? activeEdition.product === ProductCode.Free && nextEditions.length === 0
                      ? t('unlimited')
                      : t('to', {
                          validTo: getLocalizedDateWithoutLeadingZeroes(new Date(activeEdition.validTo), i18n.language),
                        })
                    : `${getLocalizedDateWithoutLeadingZeroes(
                        new Date(activeEdition.validFrom),
                        i18n.language,
                      )} - ${getLocalizedDateWithoutLeadingZeroes(new Date(activeEdition.validTo), i18n.language)}`
              }
              isFromHistoryPage={isFromHistoryPage}
              hasPreviousCreditEdition={hasPreviousCreditEdition}
            />
            {!isFromHistoryPage &&
              messages.map((edition, index) => (
                <BodySmall style={{ marginBottom: theme.spacing.d, color: theme.colors.text.secondary }} key={index}>
                  {edition}
                </BodySmall>
              ))}
          </EditionList>
        </EditionContainer>
      </CardBody>
    </Card>
  );
};

export default EditionOverviewCard;
